import { afterNextPaint } from '@helpers/after-next-paint';
import { startingPriceNumberFormat } from '@pages/VendorsSearch/utils/filterFormatting';
import { Input } from '@tkww/orion-web-ds-input';
import type { PriceRangeInterval } from '@typings/search';
import cx from 'classnames';
import React, { useMemo, useCallback, useRef } from 'react';
import { RangeSlider } from './range-slider';
import Styles from './styles.scss';

const sanitizeInput = (value: string) => value.replace(/^0+|\D/g, '');

interface StartingPriceFilterProps {
	startingMin: number;
	startingMax: number;
	minVal: number;
	maxVal: number;
	setMinVal: (value: number) => void;
	setMaxVal: (value: number) => void;
	minInput: string;
	maxInput: string;
	setMinInput: (value: string) => void;
	setMaxInput: (value: string) => void;
	step: number;
	onSetInput?: () => void;
	intervals?: PriceRangeInterval[];
}

type OrionInputChangeEvent = {
	target: {
		value: string;
	};
};

export const StartingPriceFilter = (props: StartingPriceFilterProps) => {
	const {
		startingMax,
		startingMin,
		minInput,
		maxInput,
		minVal,
		maxVal,
		setMaxInput,
		setMaxVal,
		setMinInput,
		setMinVal,
		step,
		onSetInput,
		intervals,
	} = props;

	const isMaxFocusedRef = useRef(false);

	const minInputDisplay = useMemo(() => {
		const minNumber = Number(minInput);
		if (Number.isNaN(minNumber) || !minNumber) {
			return startingPriceNumberFormat.format(startingMin);
		}

		return startingPriceNumberFormat.format(minNumber);
	}, [minInput, startingMin]);

	const maxInputDisplay = useMemo(() => {
		const maxNumber = Number(maxInput);

		if (!isMaxFocusedRef.current && !maxInput && maxVal === startingMax) {
			return `${startingPriceNumberFormat.format(startingMax)}+`;
		}

		if (Number.isNaN(maxNumber) || !maxNumber) {
			return '';
		}

		return startingPriceNumberFormat.format(maxNumber);
	}, [maxInput, startingMax, maxVal]);

	const onChangeMinInput = useCallback(
		(e: OrionInputChangeEvent) => setMinInput(sanitizeInput(e.target.value)),
		[setMinInput],
	);

	const onChangeMaxInput = useCallback(
		(e: OrionInputChangeEvent) => setMaxInput(sanitizeInput(e.target.value)),
		[setMaxInput],
	);

	const setMin = useCallback(
		(value: number) => {
			const isOutOfRange =
				Number.isNaN(value) || value <= startingMin || value >= maxVal;

			setMinVal(isOutOfRange ? startingMin : value);
			setMinInput(isOutOfRange ? '' : value.toString());

			onSetInput &&
				afterNextPaint(() => {
					onSetInput?.();
				});
		},
		[startingMin, maxVal, setMinVal, setMinInput, onSetInput],
	);

	const setMax = useCallback(
		(value: number, clampToMax = false) => {
			const isOutOfRange =
				Number.isNaN(value) ||
				value <= minVal ||
				(clampToMax && value >= startingMax);

			setMaxVal(isOutOfRange ? startingMax : value);
			setMaxInput(isOutOfRange ? '' : value.toString());

			onSetInput &&
				afterNextPaint(() => {
					onSetInput?.();
				});
		},
		[startingMax, minVal, setMaxVal, setMaxInput, onSetInput],
	);

	const setMaxClamped = useCallback(
		(value: number) => setMax(value, true),
		[setMax],
	);

	const onBlurMinInput = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			setMin(Number(sanitizeInput(e.target.value)));
		},
		[setMin],
	);

	const onFocusMaxInput = useCallback(() => {
		isMaxFocusedRef.current = true;
		if (!maxInput && maxVal === startingMax) {
			setMaxInput(startingMax.toString());
		}
	}, [maxInput, maxVal, startingMax, setMaxInput]);

	const onBlurMaxInput = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			isMaxFocusedRef.current = false;
			const newValue = Number(sanitizeInput(e.target.value));
			if (newValue !== startingMax) {
				setMax(newValue);
			} else {
				setMaxInput('');
			}
		},
		[setMax, startingMax, setMaxInput],
	);

	const onKeyDownMinInput = useCallback(
		(e) => {
			if (e.key === 'Enter') {
				setMin(Number(sanitizeInput(e.target.value)));
			}
		},
		[setMin],
	);

	const onKeyDownMaxInput = useCallback(
		(e) => {
			if (e.key === 'Enter') {
				const newValue = Number(sanitizeInput(e.target.value));
				if (newValue !== startingMax) {
					setMax(newValue);
				}
			}
		},
		[setMax, startingMax],
	);

	return (
		<div className={Styles.startingPriceContainer}>
			<RangeSlider
				startingMin={startingMin}
				startingMax={startingMax}
				minVal={minVal}
				maxVal={maxVal}
				setMinVal={setMin}
				setMaxVal={setMaxClamped}
				step={step}
				intervals={intervals}
			/>
			<div
				className={cx(
					Styles.startingPriceMinMax,
					Styles.startingPriceMinMaxLite,
				)}
			>
				<Input
					name="Minimum"
					value={minInputDisplay}
					onChange={onChangeMinInput}
					onBlur={onBlurMinInput}
					onKeyDown={onKeyDownMinInput}
				/>
				<span className={Styles.startingPriceDash}>-</span>
				<Input
					name="Maximum"
					value={maxInputDisplay}
					onChange={onChangeMaxInput}
					onFocus={onFocusMaxInput}
					onBlur={onBlurMaxInput}
					onKeyDown={onKeyDownMaxInput}
				/>
			</div>
		</div>
	);
};
